import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';

import { CardIcon, LockIcon, MailIcon, PhoneIcon } from '@icons';
import { getBrandPhoneLength } from '@utils/get-brand-phone-length';
import { getBrandPhoneMask } from '@utils/get-brand-phone-mask';

import { useMutationTwoFaLogin } from '@shared/hooks/api/auth/use-mutation-two-fa-login';

import { useMutationLocalLogin } from '@hooks/api/auth/use-mutation-local-login';
import { useMutationLogin } from '@hooks/api/auth/use-mutation-login';

import { Button } from '@components/button';
import { Input } from '@components/input';
import { Separator } from '@components/separator';

import { config } from '@/config.ts';

import { AUTH_TAB } from '../../types.ts';
import { TwoFaModal } from '../two-fa-modal';
import * as Styled from './form.styled';

type OwnProps = {
	shouldReload?: boolean;
	activeTab: AUTH_TAB;
	closeModal: () => void;
};

const isNgu3 = config.BRAND === 'ngu3';
const isBlitz = config.BRAND === 'blitz';
const isRockets = config.BRAND === 'rockets';
const isSk2 = config.BRAND === 'sk2';
const isCom2 = config.BRAND === 'com2';

const isExternalBrand = isNgu3 || isRockets || isBlitz || isSk2 || isCom2;

const Form: React.FC<OwnProps> = ({ shouldReload, activeTab, closeModal }) => {
	const { t } = useTranslation(['errors', 'reg']);

	const { mutate: login, isPending } = useMutationLogin(shouldReload);
	const { mutate: localLogin } = useMutationLocalLogin(shouldReload);
	const { mutate: twoFaLogin } = useMutationTwoFaLogin();
	const phoneLength = getBrandPhoneLength();

	const [inputValues, setInputValues] = React.useState({
		phone: '',
		email: '',
		card_number: '',
		password: ''
	});

	// field for local login
	const [pmIdValue, setPmIdValue] = React.useState<string>('');

	const ref = React.useRef(null);
	const inputRef = React.useRef(null);

	const [phoneError, setPhoneError] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string | null>(null);

	const handlePhoneChange = (value: string) => {
		setInputValues(prev => ({
			...prev,
			phone: value
		}));
		if (value.replace(/\D/g, '').length !== phoneLength) {
			setPhoneError(true);
		} else {
			setPhoneError(false);
		}
	};

	const [isTwoFaModalOpen, setIsTwoFaModalOpen] = React.useState(false);
	const [twoFASessionKey, setTwoFASessionKey] = React.useState<string | null>(null);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		if (name === 'email') {
			const cleanEmail = value.replace(/[^\w@.-]/g, '');
			setInputValues(prev => ({ ...prev, email: cleanEmail }));
		} else if (name === 'phone' || name === 'card_number' || name === 'local') {
			const cleanValue = value.replace(/[^\d\s\-()+]/g, '');
			if (name === 'local') {
				setPmIdValue(cleanValue);
			} else {
				setInputValues(prev => ({ ...prev, [name]: cleanValue }));
			}
		} else {
			setInputValues(prev => ({ ...prev, [name]: value }));
		}
	};

	const phoneMask = getBrandPhoneMask();

	const getInputId = () => {
		const rawPhone = inputValues.phone.replace(/\D/g, '');
		if (activeTab === AUTH_TAB.PHONE && rawPhone.length !== phoneLength) {
			setPhoneError(true);
			return;
		}

		const idMap: Record<AUTH_TAB, string | undefined> = {
			[AUTH_TAB.PHONE]: rawPhone,
			[AUTH_TAB.BANK_ACCOUNT]: inputValues.card_number,
			[AUTH_TAB.EMAIL]: inputValues.email,
			[AUTH_TAB.LOCAL]: pmIdValue,
			[AUTH_TAB.HELP]: undefined,
			[AUTH_TAB.REGISTER]: undefined
		};

		return idMap[activeTab];
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const id = getInputId();

		if (!id) return;

		const callbacks = {
			onSuccess: closeModal,
			onError: () => setError(t('loginError'))
		};

		// TODO: add two fa if it's restored
		// if (config.BRAND === 'pm' && import.meta.env.VITE_ENV === 'production') {
		// 	twoFaLogin(
		// 		{ id, password: inputValues.password },
		// 		{
		// 			onSuccess: (data: any) => {
		// 				setTwoFASessionKey(data.twoFASessionKey);
		// 				setIsTwoFaModalOpen(true);
		// 			},
		// 			onError: () => {
		// 				setError(t('loginError'));
		// 			}
		// 		}
		// 	);
		// }

		if (config.ENV === 'production') {
			login({ id, password: inputValues.password }, callbacks);
		} else {
			localLogin({ id }, callbacks);
		}
	};

	if (isTwoFaModalOpen) {
		const id = getInputId();

		if (!id) {
			return null;
		}

		return (
			<TwoFaModal
				isOpen={isTwoFaModalOpen}
				closeModal={() => setIsTwoFaModalOpen(false)}
				twoFASessionKey={twoFASessionKey}
				id={id}
				password={inputValues.password}
				closeAuthModal={closeModal}
			/>
		);
	}

	return (
		<Styled.FormWrapper onSubmit={handleSubmit}>
			{activeTab === AUTH_TAB.PHONE ? (
				<Styled.MaskedInputWrapper>
					<Styled.Label>
						{t('phone')} <span>*</span>
					</Styled.Label>

					<Styled.MaskedInput $hasError={phoneError}>
						<Styled.InputIcon $position='start'>
							<PhoneIcon />
						</Styled.InputIcon>
						<IMaskInput
							mask={phoneMask}
							required
							lazy={false}
							unmask={true}
							ref={ref}
							inputRef={inputRef}
							placeholderChar='X'
							value={inputValues.phone}
							onAccept={handlePhoneChange}
						/>
					</Styled.MaskedInput>
				</Styled.MaskedInputWrapper>
			) : activeTab === AUTH_TAB.BANK_ACCOUNT ? (
				<Input
					label={isExternalBrand ? 'ID' : t('parimatch_id')}
					type='text'
					isRequired
					placeholder='xxx-xxx-xxx'
					icon={CardIcon}
					required
					value={inputValues.card_number}
					name='card_number'
					onChange={handleChange}
				/>
			) : activeTab === AUTH_TAB.LOCAL ? (
				<Input
					label='ID'
					type='text'
					isRequired
					placeholder='xxx-xxx-xxx'
					icon={CardIcon}
					required
					value={pmIdValue}
					name='local'
					onChange={handleChange}
				/>
			) : (
				<Input
					label={t('main:email')}
					type='email'
					isRequired
					placeholder={t('main:email')}
					icon={MailIcon}
					required
					value={inputValues.email}
					name='email'
					onChange={handleChange}
				/>
			)}

			<Separator height={16} />
			<Input
				label={t('password')}
				type='password'
				isPassword
				isRequired
				placeholder={t('password')}
				icon={LockIcon}
				required
				value={inputValues.password}
				name='password'
				onChange={handleChange}
			/>
			<Styled.Error>{error}</Styled.Error>
			<Separator height={24} />
			<Button fluid color='primary' disabled={isPending}>
				{t('to_enter')}
			</Button>
			<Separator height={16} />
		</Styled.FormWrapper>
	);
};

export default Form;
